import React, { useState } from "react";
import {
  ArrowLeft,
  Calendar,
  User,
  Mail,
  Phone,
  Building2,
  Eye,
  Clock,
  Globe,
  Lock,
  Home,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Mock Link component
const Link = ({ to, children, className, ...props }) => (
  <a href={to} className={className} {...props}>
    {children}
  </a>
);

// Status Badge Component
const StatusBadge = ({ status, visibility, showOnHomepage }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          icon: Clock,
          label: "Pending",
        };
      case "approved":
        return {
          color: "bg-green-100 text-green-800",
          icon: Eye,
          label: "Approved",
        };
      case "rejected":
        return {
          color: "bg-red-100 text-red-800",
          icon: Eye,
          label: "Rejected",
        };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: Eye, label: status };
    }
  };

  const getVisibilityConfig = (visibility) => {
    switch (visibility) {
      case "public":
        return {
          color: "bg-green-100 text-green-800",
          icon: Globe,
          label: "Public",
        };
      case "private":
        return {
          color: "bg-orange-100 text-orange-800",
          icon: Lock,
          label: "Private",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          icon: Eye,
          label: visibility,
        };
    }
  };

  const statusConfig = getStatusConfig(status);
  const visibilityConfig = getVisibilityConfig(visibility);
  const StatusIcon = statusConfig.icon;
  const VisibilityIcon = visibilityConfig.icon;

  return (
    <div className="flex flex-wrap gap-2 mb-6">
      <div
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}
      >
        <StatusIcon className="w-4 h-4" />
        <span>{statusConfig.label}</span>
      </div>

      <div
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${visibilityConfig.color}`}
      >
        <VisibilityIcon className="w-4 h-4" />
        <span>{visibilityConfig.label}</span>
      </div>

      {showOnHomepage && (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          <Home className="w-4 h-4" />
          <span>Show on Homepage</span>
        </div>
      )}
    </div>
  );
};

// Member Details Card Component
const MemberDetailsCard = ({ member }) => {
  if (!member) return null;

  const { profileInfo, communication, organization } = member;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 flex justify-center items-center bg-[#4F22D8]/10 rounded-lg">
          <User className="w-5 h-5 text-[#4F22D8]" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Created By</h3>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden flex-shrink-0">
          <img
            src={
              profileInfo?.photo ||
              "https://via.placeholder.com/64x64?text=User"
            }
            alt={`${profileInfo?.firstName} ${profileInfo?.lastName}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-900 mb-3">
            {profileInfo?.firstName} {profileInfo?.lastName}
          </h4>

          {/* Contact Information */}
          <div className="space-y-2">
            {communication?.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-[#4F22D8]" />
                <a
                  href={`mailto:${communication.email}`}
                  className="hover:text-[#4F22D8] transition-colors"
                >
                  {communication.email}
                </a>
              </div>
            )}

            {communication?.mobile && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-[#4F22D8]" />
                <a
                  href={`tel:${communication.mobile}`}
                  className="hover:text-[#4F22D8] transition-colors"
                >
                  {communication.mobile}
                </a>
              </div>
            )}

            {organization?.organizationName && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="w-4 h-4 text-[#4F22D8]" />
                <span>{organization.organizationName}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const NewsDetailsData = ({ news, isLoading = false, isError = false }) => {
  const [isAuthenticated] = useState(true);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#4F22D8] border-t-transparent mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading news article...</p>
        </div>
      </div>
    );
  }

  if (isError || !news) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-6">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Article Not Found
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Sorry, we couldn't load the news details.
          </p>
          <Link
            to="/admin/news/list"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#4F22D8] text-white rounded-lg hover:bg-[#4F22D8]/90 transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  const {
    title,
    description,
    date,
    imageUrl,
    memberId,
    status,
    visibility,
    showOnHomepage,
    createdAt,
    updatedAt,
  } = news;

  return (
    <main className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Article Header */}
              <div className="px-6 sm:px-8 pt-8 pb-6">
                {/* Status Badges */}
                <StatusBadge
                  status={status}
                  visibility={visibility}
                  showOnHomepage={showOnHomepage}
                />

                <div className="flex items-center gap-3 text-sm text-gray-600 mb-6">
                  <div className="flex items-center gap-2 px-3 py-1 bg-[#4F22D8]/10 rounded-full">
                    <Calendar className="w-4 h-4 text-[#4F22D8]" />
                    <span className="font-medium">
                      {new Date(date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </div>

                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-8 leading-tight">
                  {title}
                </h1>
              </div>

              {/* Article Content */}
              <div className="px-6 sm:px-8 pb-8">
                <div
                  className="prose prose-lg max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              </div>

              {/* Article Metadata */}
              <div className="px-6 sm:px-8 pb-8 border-t border-gray-200 pt-6">
                <div className="text-sm text-gray-500 space-y-1">
                  <p>
                    <strong>Created:</strong>{" "}
                    {new Date(createdAt).toLocaleString()}
                  </p>
                  <p>
                    <strong>Updated:</strong>{" "}
                    {new Date(updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {imageUrl && imageUrl.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <Swiper
                  modules={[Navigation, Pagination]}
                  spaceBetween={0}
                  slidesPerView={1}
                  navigation={{
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                  }}
                  pagination={{
                    clickable: true,
                    dynamicBullets: true,
                  }}
                  loop={imageUrl.length > 1}
                  className="aspect-video news-image-slider"
                  style={{
                    "--swiper-navigation-color": "#4F22D8",
                    "--swiper-pagination-color": "#4F22D8",
                  }}
                >
                  {imageUrl.map((url, index) => (
                    <SwiperSlide key={index}>
                      <div className="relative w-full h-full">
                        <img
                          src={url}
                          alt={`News image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                      </div>
                    </SwiperSlide>
                  ))}

                  {/* Custom Navigation Buttons */}
                  {imageUrl.length > 1 && (
                    <>
                      <div className="swiper-button-prev !w-10 !h-10 !bg-white/90 hover:!bg-white !rounded-full !shadow-lg hover:!shadow-xl !transition-all !duration-200 after:!text-sm after:!font-bold after:!text-[#4F22D8]"></div>
                      <div className="swiper-button-next !w-10 !h-10 !bg-white/90 hover:!bg-white !rounded-full !shadow-lg hover:!shadow-xl !transition-all !duration-200 after:!text-sm after:!font-bold after:!text-[#4F22D8]"></div>
                    </>
                  )}
                </Swiper>
              </div>
            )}
            <MemberDetailsCard member={memberId} />
          </div>
        </div>
      </div>

      <style jsx global>{`
        .news-image-slider .swiper-pagination {
          bottom: 16px !important;
        }

        .news-image-slider .swiper-pagination-bullet {
          background: white !important;
          opacity: 0.5 !important;
          width: 8px !important;
          height: 8px !important;
        }

        .news-image-slider .swiper-pagination-bullet-active {
          opacity: 1 !important;
          background: #4f22d8 !important;
          transform: scale(1.25) !important;
        }

        .news-image-slider .swiper-button-prev,
        .news-image-slider .swiper-button-next {
          margin-top: -20px !important;
        }
      `}</style>
    </main>
  );
};

export default NewsDetailsData;
